import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { deepCopy } from 'src/app/compartido/utilidades/objetos.utils';
import { ValoresFormulario } from 'src/app/compartido/formulario/utilidades/formulario.models';
import {
  ErrorDesarrollo,
  ErrorFrontend,
} from 'src/app/compartido/utilidades/error.utils';
import { obtenerCampo } from 'src/app/compartido/formulario/utilidades/formulario.utils';
import {
  formatearFecha,
  valorNumérico,
} from 'src/app/compartido/utilidades/valores.utils';
import { BehaviorSubject, firstValueFrom, Subject, Subscription } from 'rxjs';
import { DialogosService } from 'src/app/nucleo/utilidades/dialogos.service';
import { AlertasService } from 'src/app/nucleo/utilidades/alertas.service';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { FormulariosService } from 'src/app/nucleo/utilidades/formularios.service';
import { Permiso } from 'src/app/nucleo/autorizacion/autorizacion.models';
import { AnaliticasService } from 'src/app/nucleo/utilidades/analiticas.service';
import { Parametros } from 'src/app/compartido/utilidades/compartido.models';
import { AutenticacionService } from 'src/app/nucleo/autenticacion/autenticacion.service';
import { Icono } from 'src/app/compartido/utilidades/compartido.static';
import {
  TareaDia,
  DatosTareasDia,
  DetalleTareaDia,
} from './utilidades/tareas-dia.models';
import {
  CONFIGURACION_FICHA_TAREAS_DIA,
  CONFIGURACION_FORMULARIO_TAREAS_DIA,
  DETALLES_TAREAS_DIA,
  CONFIGURACION_DIALOGO_PLANILLA_RECOGIDA,
} from './utilidades/tareas-dia.static';
import { OrganizacionService } from 'src/app/nucleo/utilidades/organizacion.service';
import { OrdenesService } from 'src/app/ordenes/utilidades/ordenes.service';
import { PlanillasService } from 'src/app/planillas/utilidades/planillas.service';
import { AutorizacionService } from 'src/app/nucleo/autorizacion/autorizacion.service';

@Component({
  templateUrl: './tareas-dia.component.html',
  styleUrls: ['./tareas-dia.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TareasDiaComponent implements OnInit, OnDestroy {
  /* Suscripción */
  private readonly _suscripcion = new Subscription();

  /* Constantes */
  protected readonly Icono = Icono;
  protected readonly Permiso = Permiso;
  protected readonly TareaDia = TareaDia;

  /* Configuración ficha */
  protected readonly configFicha = deepCopy(CONFIGURACION_FICHA_TAREAS_DIA);

  /* Formulario */
  protected configFormulario = deepCopy(CONFIGURACION_FORMULARIO_TAREAS_DIA);
  protected valorFormulario: ValoresFormulario = {};

  /* Si la fecha seleccionada es la actual */
  protected esHoy: boolean = true;

  /* Estado de carga */
  protected cargando: boolean = false;

  /* Datos */
  protected datos: DatosTareasDia | undefined;

  /* Tareas */
  protected tareas: DetalleTareaDia[] = [];

  constructor(
    private readonly _autenticacionService: AutenticacionService,
    private readonly _autorizacionService: AutorizacionService,
    private readonly _organizacionService: OrganizacionService,
    private readonly _formulariosService: FormulariosService,
    private readonly _ordenesService: OrdenesService,
    private readonly _planillasService: PlanillasService,
    private readonly _analiticasService: AnaliticasService,
    private readonly _dialogosService: DialogosService,
    private readonly _alertasService: AlertasService,
    private readonly _route: ActivatedRoute,
    private readonly _cdr: ChangeDetectorRef,
    private readonly _router: Router,
  ) {}

  ngOnInit(): void {
    // Obtener fecha
    const campoFecha = obtenerCampo(this.configFormulario, 'fecha')!;
    const campoOrg = obtenerCampo(this.configFormulario, 'organizacion')!;
    campoFecha.valorIn = new BehaviorSubject(formatearFecha(new Date()));
    campoOrg.valorIn = new Subject<number>();
    firstValueFrom(this._route.queryParams).then((params: Params) => {
      const fecha = params['fecha'];
      if (fecha) campoFecha.valorIn!.next(formatearFecha(fecha));
      const organizacion = valorNumérico(params['organizacion']);
      if (organizacion) campoOrg.valorIn!.next(organizacion);
    });

    // Poblar opciones de organización
    this._formulariosService.configurarOpcionesOrganizacion$(
      this.configFormulario,
    );
  }

  ngOnDestroy(): void {
    this._suscripcion.unsubscribe();
  }

  /**
   * Cargar los datos de las tareas del día para la organización seleccionada.
   * @author Juan Corral
   */
  protected async cargarDatos$(): Promise<void> {
    const fecha = this.valorFormulario['fecha'];
    const idOrg = this.valorFormulario['organizacion'];
    if (!fecha || !idOrg) return;

    // Agregar a query params
    const queryParams: Params = { fecha: fecha, organizacion: idOrg };
    this._router.navigate([], { queryParams });

    // Establecer estado de carga
    this.cargando = true;

    // Establecer si la fecha seleccionada es la actual
    this.esHoy = formatearFecha(new Date()) === fecha;

    // Obtener datos
    this.datos = await this._analiticasService
      .obtenerTareasDelDia$(fecha, idOrg)
      .catch((error: ErrorFrontend) => {
        this._alertasService.alertarError('Error: ' + error.message);
        return undefined;
      });

    // Definir tareas
    if (this.datos !== undefined)
      this.tareas = DETALLES_TAREAS_DIA.filter(
        (tarea: DetalleTareaDia) => this.datos![tarea.slug] !== undefined,
      );

    // Establecer estado de carga
    this.cargando = false;
    this._cdr.markForCheck();
  }

  /**
   * Realizar una tarea del día.
   * @param {TareaDia} tarea - La tarea a realizar.
   * @author Juan Corral
   */
  protected realizarTarea(tarea: TareaDia): void {
    const queryParams = {
      organizacion: this.valorFormulario['organizacion'],
    };

    switch (tarea) {
      case TareaDia.CREACION:
        this._crearOrdenes$();
        break;
      case TareaDia.MIPRES:
        this._router.navigate(['evedisa-mipres']);
        break;
      case TareaDia.PREPARACION:
        this._router.navigate(['ordenes', 'preparar'], { queryParams });
        break;
      case TareaDia.ETIQUETAS:
        this._router.navigate(['ordenes', 'imprimir-etiquetas'], {
          queryParams: { ...queryParams, fecha: this.valorFormulario['fecha'] },
        });
        break;
      case TareaDia.PLANILLA_RECOGIDA:
        this._descargarPlanilla$();
        break;
      case TareaDia.RECOGIDA:
        this._router.navigate(['ordenes', 'recoger'], { queryParams });
        break;
      case TareaDia.ENRUTAMIENTO:
        window.open(
          'https://app2.simpliroute.com/#/planner/vehicles',
          '_blank',
        );
        break;
      case TareaDia.DESPACHO:
        this._router.navigate(['domicilios', 'despachar'], { queryParams });
        break;
      case TareaDia.ENTREGA:
        this._router.navigate(['domicilios', 'entregar'], { queryParams });
        break;
      case TareaDia.LEGALIZACION:
        this._router.navigate(['planillas', 'legalizar'], { queryParams });
        break;
    }
  }

  /**
   * Navega a la página de creación de órdenes.
   * @author Juan Corral
   */
  private async _crearOrdenes$(): Promise<void> {
    // Si es aliado, redirigir a la página de solicitudes
    const usuario = await this._autenticacionService.usuario$;
    if (usuario?.aliado) {
      this._router.navigate(['solicitudes', 'ep']);
      return;
    }

    // Obtener permisos
    const permisoIndividual = await this._autorizacionService.tienePermiso$(
      'orden',
      Permiso.CREAR,
    );
    const permisoMasivo = await this._autorizacionService.tienePermiso$(
      'orden',
      Permiso.CREAR_MASIVO,
    );

    // Determinar página a navegar
    let pagina: string | undefined;
    if (permisoIndividual && !permisoMasivo) pagina = 'crear';
    else if (!permisoIndividual && permisoMasivo) pagina = 'importar';
    else if (permisoIndividual && permisoMasivo) {
      // Abrir dialogo de selección
      const resultado = await this._dialogosService.abrirDialogo$(
        '¿Cómo deseas crear las órdenes?',
        'Escoge una de las siguientes opciones:',
        'Importar Archivo',
        'Individualmente',
        true,
        true,
      );
      if (resultado === undefined) return;
      pagina = resultado ? 'importar' : 'crear';
    }

    // Navegar a la página
    const queryParams = {
      organizacion: this.valorFormulario['organizacion'],
    };
    this._router.navigate(['ordenes', pagina], { queryParams });
  }

  /**
   * Descarga la planilla de las órdenes recogidas en el día.
   * @author Juan Corral
   */
  private async _descargarPlanilla$(): Promise<void> {
    if (!this.datos) return;
    // Definir origen
    const origen = await this._determinarOrigen$();
    if (origen === undefined) return;

    // Obtener planilla
    const params: Parametros = {
      organizacion: this.valorFormulario['organizacion'],
      fecha: this.valorFormulario['fecha'],
    };
    if (origen && !isNaN(origen)) params['sede'] = origen;
    const planillas = await this._planillasService.obtenerPlanillas$(params);
    if (planillas.count === 0) {
      this._alertasService.alertarError(
        'Aún no hay órdenes recogidas, debes esperar a que el transportista recoja algunas órdenes primero.',
      );
      return;
    }
    const planilla = planillas.results[0];

    // Definir cantidad de órdenes en la planilla
    const ordenes = await this._ordenesService
      .obtenerOrdenes$({ planilla: planilla.id })
      .catch((error: ErrorFrontend) => {
        this._alertasService.alertarError('Error: ' + error.message);
        return undefined;
      });
    if (!ordenes) return;
    const cantidad = ordenes.count;
    const s = cantidad > 1 ? 's' : '';
    const orden = cantidad > 1 ? 'órdenes' : 'orden';

    // Abrir dialogo de confirmación
    this._dialogosService
      .abrirDialogo$(
        `¿Descargar planilla con ${cantidad} órden${s}?`,
        `Se descargará un archivo Excel con los detalles de la${s} ${orden} recogidas en este día.`,
        'Confirmar',
        'Cancelar',
      )
      .then((confirmado: boolean) => {
        if (!confirmado) return;

        this._alertasService.alertar(
          'Generando el Excel, esto puede tardar unos segundos...',
        );

        this._planillasService
          .descargarPlanilla$(planilla.id, 'recogida')
          .then(() => {
            this._alertasService.alertarExito(
              'El archivo ha sido descargado correctamente',
            );
            this.cargarDatos$();
          })
          .catch((error: ErrorFrontend) =>
            this._alertasService.alertarError('Error: ' + error.message),
          );
      });
  }

  /**
   * Solicita al usuario seleccionar un origen.
   * @returns {Promise<number | undefined>} El ID del origen seleccionado o undefined si no se seleccionó ninguno (promise).
   * @author Juan Corral
   */
  private async _determinarOrigen$(): Promise<number | undefined> {
    const usuario = await this._autenticacionService.usuario$;
    if (!usuario)
      throw new ErrorDesarrollo('No se ha podido obtener el usuario');

    // Si el usuario tiene una sede asignada, se devuelve el ID de la sede
    if (usuario.empleado?.sede) return usuario.empleado.sede.id;

    // Chequear si la organización tiene sedes
    const org = (await this._organizacionService.organizaciones$).find(
      (o) => o.id === this.valorFormulario['organizacion'],
    )!;
    const sedes = await this._organizacionService.obtenerSedes$(org);
    if (sedes.length === 0) return NaN;

    // Si la organización tiene sedes, se abre el dialogo de selección
    const config = deepCopy(CONFIGURACION_DIALOGO_PLANILLA_RECOGIDA);
    this._formulariosService.configurarOpcionesOrganizacion$(config.formulario);
    this._formulariosService
      .configurarOpcionesOrigen$(config.formulario, false, true)
      .then((suscripcion: Subscription) => this._suscripcion.add(suscripcion));
    const campoOrg = obtenerCampo(config.formulario, 'organizacion')!;
    campoOrg.valorIn = new BehaviorSubject(org.id);
    const resultado = await this._dialogosService.abrirFormulario$(config);
    return resultado?.['origen'];
  }
}
